import React from 'react';
import { calculateAge } from './utils';

import './style.css';
import type { PatientTO } from '../../../../../lib';
import CareMapprDropdown from '../../../Dropdown/CareMapprDropdown';
import { useModal, useTriggerUpdate } from '../../../../../context';
import CareMapprButton from '../../../Button/CareMapprButton';
import { useAssignPatient } from '../../../../hooks';

interface AssignToSomeoneElseProps {
  patient: PatientTO;
  isPatientProfile?: boolean;
  clinicianOptions: ClinicianOption[];
}

export type ClinicianOption = { label: string; value: number };

const AssignToSomeoneElse = ({
  patient,
  isPatientProfile,
  clinicianOptions,
}: AssignToSomeoneElseProps) => {
  const [selectedClinician, setSelectedClinician] = React.useState<number>();
  const { closeModal } = useModal();
  const { assignToClinician } = useAssignPatient();
  const { triggerPatientListUpdate, triggerPatientProfileUpdate } =
    useTriggerUpdate();

  const patientName = React.useMemo(
    () => `${patient.first_name} ${patient.last_name}`,
    [patient],
  );
  const patientAge = React.useMemo(() => {
    const age = calculateAge(patient.dob);
    if (age) {
      return `${age}yrs`;
    }
    return '';
  }, [patient]);

  const patientUR = React.useMemo(() => {
    if (!patient.client_no) {
      return '';
    }
    const [clientNumber] = patient.client_no;
    if (!clientNumber) {
      return '';
    }
    const { client_no: clientNo } = clientNumber;
    if (!clientNo) {
      return '';
    }
    return clientNo;
  }, [patient]);

  const onSelectClinician = (option: ClinicianOption) => {
    setSelectedClinician(option.value);
  };

  const onAssignToSomeoneElse = async () => {
    if (!selectedClinician) {
      closeModal();
      return;
    }
    assignToClinician(
      true,
      patient.access_token,
      selectedClinician,
      isPatientProfile ? triggerPatientProfileUpdate : triggerPatientListUpdate,
    );
    closeModal();
  };

  return (
    <>
      <div className="d-flex flex-column justify-content-center gap-4">
        <div className="assign-to-header">
          Assign patient: {patientName} ({patientAge}, {patientUR})
        </div>

        <CareMapprDropdown
          label="Select the team member to assign this patient to"
          labelClassName="assign-to-option-label"
          options={clinicianOptions}
          value={clinicianOptions.find(
            (opt) => opt.value === selectedClinician,
          )}
          onChange={onSelectClinician}
        />
      </div>
      <div className="d-flex gap-2 justify-content-end">
        <CareMapprButton
          label="Cancel & close"
          variant="transparent"
          onClick={closeModal}
        />
        <CareMapprButton label="Save change" onClick={onAssignToSomeoneElse} />
      </div>
    </>
  );
};

export default AssignToSomeoneElse;
